import React, { useMemo, useRef, useState } from 'react';
import { Select, Spin } from 'antd';
import debounce from 'lodash/debounce';

/**
 * Async typeahead Select — configurable min search length (default 2).
 * Merges externally supplied options (e.g. selected values) with search hits.
 */
export function CheckinTypeTypeahead({
  fetchOptions,
  minLength = 2,
  debounceTimeout = 400,
  options: externalOptions = [],
  ...selectProps
}) {
  const [fetching, setFetching] = useState(false);
  const [searchOptions, setSearchOptions] = useState([]);
  const fetchRef = useRef(0);

  const mergedOptions = useMemo(() => {
    const map = new Map();
    (externalOptions || []).forEach((o) => {
      if (o?.value != null) map.set(o.value, o);
    });
    searchOptions.forEach((o) => {
      if (o?.value != null) map.set(o.value, o);
    });
    return [...map.values()];
  }, [externalOptions, searchOptions]);

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value) => {
      if (!value || value.length < minLength) {
        setSearchOptions([]);
        return;
      }
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setFetching(true);
      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) return;
        setSearchOptions(Array.isArray(newOptions) ? newOptions : []);
        setFetching(false);
      }).catch(() => {
        if (fetchId !== fetchRef.current) return;
        setSearchOptions([]);
        setFetching(false);
      });
    };
    return debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout, minLength]);

  return (
    <Select
      showSearch
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={
        fetching
          ? <Spin size="small" />
          : (selectProps.value != null && selectProps.value !== '' ? null : `Type at least ${minLength} characters`)
      }
      {...selectProps}
      options={mergedOptions}
    />
  );
}

export default CheckinTypeTypeahead;
